import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Loader2, ShieldX } from "lucide-react";

function isAccessDeniedError(error: Error): boolean {
  const msg = error.message?.toLowerCase() || "";
  return msg.includes("access_denied") || msg.includes("presignup");
}

export default function SigninCallbackPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (auth.error) {
      console.error("Auth callback error:", auth.error);

      if (isAccessDeniedError(auth.error)) {
        setAccessDenied(true);
        return;
      }

      navigate("/login", { replace: true });
      return;
    }

    if (!auth.isLoading && auth.isAuthenticated) {
      const hostname = window.location.hostname;
      const isPortal = /[-.]portal\./.test(hostname) || hostname.startsWith("portal.");

      if (isPortal) {
        navigate("/", { replace: true });
      } else {
        window.location.href = `https://${import.meta.env.VITE_PORTAL_URL}/`;
      }
    }
  }, [auth, navigate]);

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldX className="w-8 h-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Access Denied
            </h1>
            <p className="text-muted-foreground">
              Your account is not associated with an active client. Only
              authorized clients can access the portal.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            If you believe this is an error, please contact us at{" "}
            <a
              href="mailto:support@echopeaklabs.com"
              className="text-primary underline underline-offset-4 hover:text-primary/80"
            >
              support@echopeaklabs.com
            </a>
          </p>
          <a
            href="/login"
            className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
